import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

// ── Validação de CPF ──────────────────────────────────────────────────────────
function validateCpf(raw) {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(digits[i]) * (10 - i);
  let r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  if (r !== Number(digits[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += Number(digits[i]) * (11 - i);
  r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  return r === Number(digits[10]);
}

// ── Validação de senha ────────────────────────────────────────────────────────
function validateSenha(senha) {
  const errors = [];
  if (senha.length < 8)        errors.push('mínimo 8 caracteres');
  if (!/[A-Z]/.test(senha))    errors.push('pelo menos uma letra maiúscula');
  if (!/[a-z]/.test(senha))    errors.push('pelo menos uma letra minúscula');
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(senha))
    errors.push('pelo menos um caractere especial');
  return errors;
}

// ── Máscara de CPF ────────────────────────────────────────────────────────────
function maskCpf(value) {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
  return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
}

const s = {
  page: { display: 'flex', minHeight: '100vh', background: 'var(--bg)' },
  left: {
    flex: 1, background: 'var(--navy)', display: 'flex', alignItems: 'center',
    padding: '60px 64px',
  },
  brand: { maxWidth: 440 },
  tagline: { fontSize: 12, fontWeight: 600, letterSpacing: '.12em', color: 'rgba(255,255,255,.5)', textTransform: 'uppercase' },
  h1: { fontSize: 36, fontWeight: 800, color: '#fff', margin: '16px 0 20px', lineHeight: 1.2 },
  p: { fontSize: 16, color: 'rgba(255,255,255,.65)', lineHeight: 1.7 },
  right: {
    width: 480, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '48px 40px',
  },
  wrap: { width: '100%' },
  tabs: { display: 'flex', borderBottom: '2px solid var(--border)', marginBottom: 28 },
  tab: {
    flex: 1, padding: '10px 0', border: 'none', background: 'none', cursor: 'pointer',
    fontSize: 15, fontWeight: 600, color: 'var(--text-2)', transition: 'color .15s',
    borderBottom: '2px solid transparent', marginBottom: -2,
  },
  tabActive: { color: 'var(--navy)', borderBottomColor: 'var(--navy)' },
  h2: { fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' },
  sub: { fontSize: 13, color: 'var(--text-3)', margin: '0 0 24px' },
  success: {
    textAlign: 'center', padding: '32px 0',
  },
  successIcon: { fontSize: 48, marginBottom: 16 },
  successTitle: { fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 12 },
  successText: { fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 24 },
};

export default function LoginPage() {
  const { signIn, signUp } = useAuth();
  const [tab, setTab] = useState('login');

  // ── login state ─────────────────────────────────────────────────────────
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError]     = useState('');

  async function handleLogin(e) {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    const { error } = await signIn(email, password);
    if (error) setLoginError('Email ou senha incorretos.');
    setLoginLoading(false);
  }

  // ── registro state ──────────────────────────────────────────────────────
  const [regNome, setRegNome]                   = useState('');
  const [regEmail, setRegEmail]                 = useState('');
  const [regCpf, setRegCpf]                     = useState('');
  const [regSenha, setRegSenha]                 = useState('');
  const [regConfirmar, setRegConfirmar]         = useState('');
  const [regErrors, setRegErrors]               = useState({});
  const [regLoading, setRegLoading]             = useState(false);
  const [regSuccess, setRegSuccess]             = useState(false);

  function handleCpfChange(e) {
    setRegCpf(maskCpf(e.target.value));
  }

  async function handleRegister(e) {
    e.preventDefault();
    const errors = {};

    if (!regNome.trim() || regNome.trim().length < 3)
      errors.nome = 'Nome deve ter pelo menos 3 caracteres.';

    if (!regEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail))
      errors.email = 'Informe um e-mail válido.';

    const cpfRaw = regCpf.replace(/\D/g, '');
    if (!validateCpf(cpfRaw))
      errors.cpf = 'CPF inválido.';

    const senhaErrors = validateSenha(regSenha);
    if (senhaErrors.length > 0)
      errors.senha = `A senha precisa ter: ${senhaErrors.join(', ')}.`;

    if (regSenha !== regConfirmar)
      errors.confirmar = 'As senhas não coincidem.';

    if (Object.keys(errors).length > 0) {
      setRegErrors(errors);
      return;
    }

    setRegErrors({});
    setRegLoading(true);
    try {
      const { error } = await signUp(regEmail, regSenha, regNome.trim(), cpfRaw);
      if (error) {
        const msg = error.message || '';
        if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already been registered'))
          setRegErrors({ email: 'Este e-mail já está cadastrado.' });
        else if (msg.includes('profiles_cpf_unique'))
          setRegErrors({ cpf: 'Este CPF já está cadastrado.' });
        else
          setRegErrors({ geral: msg });
        return;
      }
      setRegSuccess(true);
    } finally {
      setRegLoading(false);
    }
  }

  // ── render ──────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <style>{`
        @media (max-width: 768px) {
          .login-left-panel { display: none !important; }
          .login-right-panel {
            width: 100% !important;
            padding: 40px 24px !important;
            background: var(--navy) !important;
          }
          .login-form-card {
            background: var(--surface);
            border-radius: 20px;
            padding: 28px 20px;
          }
          .login-form-card .form-input { font-size: 16px !important; }
        }
        .login-tab:hover { color: var(--navy) !important; }
      `}</style>

      {/* Lado esquerdo */}
      <div className="login-left-panel" style={s.left}>
        <div style={s.brand}>
          <span style={s.tagline}>INTO · Rio de Janeiro</span>
          <h1 style={s.h1}>Sistema de Gestão<br />de Pesquisa Clínica</h1>
          <p style={s.p}>Plataforma integrada para coleta, acompanhamento e análise de dados em pesquisas cirúrgicas de coluna e ortopedia.</p>
        </div>
      </div>

      {/* Lado direito */}
      <div className="login-right-panel" style={s.right}>
        <div className="login-form-card" style={s.wrap}>

          {/* Abas */}
          <div style={s.tabs}>
            {[['login', 'Entrar'], ['cadastro', 'Cadastrar-se']].map(([id, label]) => (
              <button
                key={id}
                className="login-tab"
                style={{ ...s.tab, ...(tab === id ? s.tabActive : {}) }}
                onClick={() => { setTab(id); setLoginError(''); setRegErrors({}); setRegSuccess(false); }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* ── Aba Login ── */}
          {tab === 'login' && (
            <>
              <h2 style={s.h2}>Bem-vindo</h2>
              <p style={s.sub}>Acesso restrito a pesquisadores autorizados</p>
              {loginError && <div className="alert alert-error">⚠ {loginError}</div>}
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label className="form-label">E-mail institucional</label>
                  <input className="form-input" type="email" value={email}
                    onChange={e => setEmail(e.target.value)} placeholder="seu@email.com" required autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label">Senha</label>
                  <input className="form-input" type="password" value={password}
                    onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
                </div>
                <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '11px' }}
                  type="submit" disabled={loginLoading}>
                  {loginLoading ? <><span className="spinner" />Entrando...</> : 'Entrar'}
                </button>
              </form>
            </>
          )}

          {/* ── Aba Cadastro ── */}
          {tab === 'cadastro' && !regSuccess && (
            <>
              <h2 style={s.h2}>Criar conta</h2>
              <p style={s.sub}>Preencha os dados para solicitar acesso</p>

              {regErrors.geral && <div className="alert alert-error">⚠ {regErrors.geral}</div>}

              <form onSubmit={handleRegister}>
                <div className="form-group">
                  <label className="form-label">Nome completo *</label>
                  <input className="form-input" value={regNome}
                    onChange={e => setRegNome(e.target.value)} placeholder="Seu nome completo" />
                  {regErrors.nome && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{regErrors.nome}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">E-mail *</label>
                  <input className="form-input" type="email" value={regEmail}
                    onChange={e => setRegEmail(e.target.value)} placeholder="seu@email.com" />
                  {regErrors.email && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{regErrors.email}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">CPF *</label>
                  <input className="form-input" value={regCpf}
                    onChange={handleCpfChange} placeholder="000.000.000-00"
                    inputMode="numeric" maxLength={14} />
                  {regErrors.cpf && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{regErrors.cpf}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Senha *</label>
                  <input className="form-input" type="password" value={regSenha}
                    onChange={e => setRegSenha(e.target.value)} placeholder="••••••••" />
                  {regErrors.senha
                    ? <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{regErrors.senha}</div>
                    : <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                        Mín. 8 caracteres, letras maiúsculas, minúsculas e um caractere especial.
                      </div>
                  }
                </div>

                <div className="form-group">
                  <label className="form-label">Confirmar senha *</label>
                  <input className="form-input" type="password" value={regConfirmar}
                    onChange={e => setRegConfirmar(e.target.value)} placeholder="••••••••" />
                  {regErrors.confirmar && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{regErrors.confirmar}</div>}
                </div>

                <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '11px' }}
                  type="submit" disabled={regLoading}>
                  {regLoading ? <><span className="spinner" />Cadastrando...</> : 'Criar conta'}
                </button>
              </form>
            </>
          )}

          {/* ── Sucesso no cadastro ── */}
          {tab === 'cadastro' && regSuccess && (
            <div style={s.success}>
              <div style={s.successIcon}>✓</div>
              <div style={s.successTitle}>Cadastro realizado!</div>
              <p style={s.successText}>
                Sua conta foi criada com sucesso. Aguarde a aprovação de um administrador para acessar o sistema.
              </p>
              <button className="btn btn-secondary" onClick={() => { setTab('login'); setRegSuccess(false); }}>
                Ir para o login
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
